// src-lit/components/product/kd-variant-synced-element.ts
import { LitElement, nothing } from 'lit';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';

/**
 * VARIANT_SELECTED を購読して per-variant データで Light DOM を差し替える Lit 要素の共通基底。
 *
 * 派生クラスは下記 2 つを実装する:
 *   - variantMapSelector: 全 variant の状態を埋め込んだ `<script type="application/json">` の selector
 *   - onVariantSelected(entry, detail): 引いたエントリで子 DOM を更新する処理
 *
 * 既存の kd-product-badge-group も同じ scaffold を持つが移行は別 issue にする（diff スコープ外）。
 */
export abstract class KdVariantSyncedElement<TEntry> extends LitElement {
  protected abstract readonly variantMapSelector: string;

  protected abstract onVariantSelected(
    entry: TEntry,
    detail: VariantSelectedDetail,
  ): void;

  private unsubscribe: (() => void) | null = null;

  protected variantMap: Record<string, TEntry | undefined> = {};

  // Shopify Section が出力する SSR 済み Light DOM をそのまま操作するため shadow DOM は使わない
  createRenderRoot() {
    return this;
  }

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.loadVariantMap();
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => {
        const entry = this.variantMap[e.detail.variantId];
        // entry が primitive 型に拡張されたとき falsy 値で skip しないよう undefined 比較を明示
        if (entry !== undefined) this.onVariantSelected(entry, e.detail);
      },
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private loadVariantMap(): void {
    const script = this.querySelector<HTMLScriptElement>(this.variantMapSelector);
    if (!script?.textContent) return;
    try {
      this.variantMap = JSON.parse(script.textContent);
    } catch {
      // JSON parse 失敗時は空マップのまま
    }
  }
}
